import api from './api';

/**
 * Get all projects
 * @returns {Promise<Array>} Array of projects
 */
export const getProjects = async () => {
  try {
    const response = await api.get('/projects');
    return response.data;
  } catch (error) {
    console.error('Error fetching projects:', error);
    throw error;
  }
};

/**
 * Get a project by ID
 * @param {number} id - Project ID
 * @returns {Promise<Object>} Project object
 */
export const getProject = async (id) => {
  try {
    const response = await api.get(`/projects/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching project ${id}:`, error);
    throw error;
  }
};

/**
 * Create a new project
 * @param {Object} projectData - Project data
 * @returns {Promise<Object>} Created project
 */
export const createProject = async (projectData) => {
  try {
    const response = await api.post('/projects', projectData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Update a project
 * @param {number} id - Project ID
 * @param {Object} projectData - Updated project data
 * @returns {Promise<Object>} Updated project
 */
export const updateProject = async (id, projectData) => {
  try {
    const response = await api.put(`/projects/${id}`, projectData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Delete a project
 * @param {number} id - Project ID
 * @returns {Promise<Object>} Response object
 */
export const deleteProject = async (id) => {
  try {
    const response = await api.delete(`/projects/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting project ${id}:`, error);
    throw error;
  }
};

/**
 * Get all chats for a project with pagination and optional server-side filters
 * @param {number} id - Project ID
 * @param {Object} options - Query options
 * @param {number} [options.page] - Page number (default: 1)
 * @param {number} [options.pageSize] - Items per page (default: 100)
 * @param {string} [options.search] - Free-text match against title / username
 * @param {string} [options.category] - Filter by chat category
 * @param {string} [options.language] - Filter by language
 * @param {string} [options.country] - Filter by country (single value or comma list)
 * @param {string} [options.assignment_type] - Filter by 'manual' | 'automatic'
 * @returns {Promise<Object>} Object with chats array, pagination info, and applied filters
 */
export const getProjectChats = async (id, options = {}) => {
  try {
    const {
      page = 1,
      pageSize = 100,
      search,
      category,
      language,
      country,
      assignment_type,
    } = options;

    const params = { page, pageSize };
    if (search) params.search = search;
    if (category) params.category = category;
    if (language) params.language = language;
    if (country) params.country = country;
    if (assignment_type) params.assignment_type = assignment_type;

    const response = await api.get(`/projects/${id}/chats`, { params });
    return response.data;
  } catch (error) {
    console.error(`Error fetching chats for project ${id}:`, error);
    throw error;
  }
};

/**
 * Add chats to a project
 * @param {number} id - Project ID
 * @param {Array<number>} chatIds - Array of chat IDs
 * @returns {Promise<Object>} Response object
 */
export const addChatsToProject = async (id, chatIds) => {
  try {
    const response = await api.post(`/projects/${id}/chats`, { chat_ids: chatIds });
    return response.data;
  } catch (error) {
    console.error(`Error adding chats to project ${id}:`, error);
    throw error;
  }
};

/**
 * Remove a chat from a project
 * @param {number} projectId - Project ID
 * @param {number} chatId - Chat ID
 * @returns {Promise<Object>} Response object
 */
export const removeChatFromProject = async (projectId, chatId) => {
  try {
    const response = await api.delete(`/projects/${projectId}/chats/${chatId}`);
    return response.data;
  } catch (error) {
    console.error(`Error removing chat ${chatId} from project ${projectId}:`, error);
    throw error;
  }
};

/**
 * Bulk-remove chats from a project. Pass an explicit list of chat_ids, OR
 * a filters object that mirrors the GET /:id/chats query shape — the server
 * resolves matching chat_ids so the client does not have to paginate.
 * @param {number} projectId - Project ID
 * @param {Object} payload - { chat_ids?: number[] } | { filters: { search?, category?, language?, country?, assignment_type? } }
 * @returns {Promise<Object>} { removed, chat_ids, truncated, requested }
 */
export const bulkRemoveProjectChats = async (projectId, payload) => {
  try {
    const response = await api.post(
      `/projects/${projectId}/chats/bulk-remove`,
      payload
    );
    return response.data;
  } catch (error) {
    console.error(`Error bulk-removing chats from project ${projectId}:`, error);
    throw error;
  }
};

/**
 * Test a message against keywords
 * @param {string} message - Message to test
 * @param {Array<string>} keywords - Keywords to test against
 * @returns {Promise<Object>} Test results
 */
export const testKeyword = async (message, keywords) => {
  try {
    const response = await api.post('/projects/test-keyword', { message, keywords });
    return response.data;
  } catch (error) {
    console.error('Error testing keyword:', error);
    throw error;
  }
};

/**
 * Get all available themes
 * @returns {Promise<Array>} Array of themes
 */
export const getThemes = async () => {
  try {
    const response = await api.get('/project-auto-assignment/themes');
    return response.data;
  } catch (error) {
    console.error('Error fetching themes:', error);
    throw error;
  }
};

/**
 * Get auto-assignment configuration for a project
 * @param {number} id - Project ID
 * @returns {Promise<Object>} Configuration object with themes and filters
 */
export const getAutoAssignmentConfiguration = async (id) => {
  try {
    const response = await api.get(`/project-auto-assignment/${id}/configuration`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching auto-assignment configuration for project ${id}:`, error);
    throw error;
  }
};

/**
 * Set auto-assignment configuration and trigger mass assignment
 * @param {number} id - Project ID
 * @param {Object} configuration - Configuration object
 * @param {Array<number>} configuration.themes - Array of theme IDs
 * @param {Array<Object>} configuration.filters - Array of filter objects
 * @returns {Promise<Object>} Assignment result
 */
export const setAutoAssignmentConfiguration = async (id, configuration) => {
  try {
    const response = await api.post(`/project-auto-assignment/${id}/configuration`, configuration);
    return response.data;
  } catch (error) {
    console.error(`Error setting auto-assignment configuration for project ${id}:`, error);
    throw error;
  }
};

/**
 * Run auto-assignment with current configuration
 * @param {number} id - Project ID
 * @returns {Promise<Object>} Assignment result
 */
export const runAutoAssignment = async (id) => {
  try {
    const response = await api.post(`/project-auto-assignment/${id}/run`);
    return response.data;
  } catch (error) {
    console.error(`Error running auto-assignment for project ${id}:`, error);
    throw error;
  }
};

/**
 * Get active or latest auto-assignment process for a project
 * @param {number} id - Project ID
 * @returns {Promise<Object>} Process status payload
 */
export const getAutoAssignmentStatus = async (id) => {
  try {
    const response = await api.get(`/project-auto-assignment/${id}/status`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching auto-assignment status for project ${id}:`, error);
    throw error;
  }
};

/**
 * Get statistics for project chats by category
 * @param {number} id - Project ID
 * @returns {Promise<Object>} Statistics object
 */
export const getProjectChatStats = async (id) => {
  try {
    const response = await api.get(`/projects/${id}/chats/stats`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching chat stats for project ${id}:`, error);
    throw error;
  }
};

/**
 * Get statistics for available groups to add to projects
 * @param {number} projectId - Project ID to exclude already assigned groups
 * @returns {Promise<Object>} Statistics object
 */
export const getAvailableGroupsStats = async (projectId = null) => {
  try {
    const params = projectId ? { projectId } : {};
    const response = await api.get('/projects/available-groups/stats', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching available groups stats:', error);
    throw error;
  }
};

/**
 * Get preview of auto-assignment results with themes and filters
 * @param {number} projectId - Project ID
 * @param {Array<number>} themes - Array of theme IDs
 * @param {Array<Object>} filters - Array of filter objects
 * @returns {Promise<Object>} Preview statistics
 */
export const getAutoAssignmentPreview = async (projectId, themes, filters = []) => {
  try {
    const response = await api.post('/projects/auto-assignment/preview', {
      projectId,
      themes,
      filters
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching auto-assignment preview:', error);
    throw error;
  }
};

export const getGroupFilterOptions = async () => {
  try {
    const response = await api.get('/groups/filter-options');
    return response.data;
  } catch (error) {
    console.error('Error fetching group filter options:', error);
    throw error;
  }
};

// ---------------------------------------------------------------------------
// Auto-provisioning (bot + forum group + topic) — backend route group:
//   /backend/project-provisioning/*
// ---------------------------------------------------------------------------

export const getAvailableProvisioningAccounts = async () => {
  const response = await api.get('/project-provisioning/available-accounts');
  return response.data; // { success, data: [...] }
};

export const provisionProject = async ({ name, bot_token, account_id }) => {
  const response = await api.post('/project-provisioning/provision', {
    name,
    bot_token,
    account_id,
  });
  return response.data;
};

export const getProjectProvisioningLog = async (projectId) => {
  const response = await api.get(`/project-provisioning/${projectId}/provisioning-log`);
  return response.data;
};

export const assignProjectHumanAdmin = async (projectId, userId) => {
  const response = await api.post(`/project-provisioning/${projectId}/human-admin`, {
    user_id: userId,
  });
  return response.data;
};

export const revokeProjectHumanAdmin = async (projectId) => {
  const response = await api.delete(`/project-provisioning/${projectId}/human-admin`);
  return response.data;
};

export const getProjectCategories = async (projectId) => {
  const response = await api.get(`/projects/${projectId}/categories`);
  return response.data;
};

export const generateProjectKeywords = async (projectId, payload) => {
  const response = await api.post(`/projects/${projectId}/keyword-generation`, payload);
  return response.data;
};

export const getPreissueRelevanceSettings = async (projectId) => {
  const response = await api.get(`/projects/${projectId}/preissue-relevance`);
  return response.data;
};

export const updatePreissueRelevanceSettings = async (projectId, payload) => {
  const response = await api.put(`/projects/${projectId}/preissue-relevance`, payload);
  return response.data;
};

export const previewPreissueRelevance = async (projectId, payload) => {
  const response = await api.post(`/projects/${projectId}/preissue-relevance/preview`, payload);
  return response.data;
};

export const createProjectCategory = async (projectId, payload) => {
  const response = await api.post(`/projects/${projectId}/categories`, payload);
  return response.data;
};

export const updateProjectCategory = async (projectId, categoryId, payload) => {
  const response = await api.put(`/projects/${projectId}/categories/${categoryId}`, payload);
  return response.data;
};

export const deleteProjectCategory = async (projectId, categoryId) => {
  const response = await api.delete(`/projects/${projectId}/categories/${categoryId}`);
  return response.data;
};

export const getProjectTestRunStatus = async (projectId) => {
  const response = await api.get(`/projects/${projectId}/test-run`);
  return response.data;
};

export const startProjectTestRun = async (projectId, keywordConfigRevision) => {
  const response = await api.post(`/projects/${projectId}/test-run/start`, {
    keyword_config_revision: keywordConfigRevision,
  });
  return response.data;
};

export const refreshForumStatus = async (projectId, accountId) => {
  const response = await api.post(
    `/project-provisioning/${projectId}/refresh-forum-status`,
    accountId ? { account_id: accountId } : {}
  );
  return response.data;
};

export const promoteToMain = async (projectId, { account_id, confirm = false } = {}) => {
  const response = await api.post(`/project-provisioning/${projectId}/promote-to-main`, {
    account_id,
    confirm,
  });
  return response.data;
};

export const attachToForum = async (projectId, { account_id, parent_project_id, confirm = false } = {}) => {
  const response = await api.post(`/project-provisioning/${projectId}/attach-to-forum`, {
    account_id,
    parent_project_id,
    confirm,
  });
  return response.data;
};

export const migrateToForum = async (projectId, { mode, new_project_name, category_name, target_project_id, confirm = false } = {}) => {
  const response = await api.post(`/project-provisioning/${projectId}/migrate-to-forum`, {
    mode,
    new_project_name,
    category_name,
    target_project_id,
    confirm,
  });
  return response.data;
};

export default {
  getProjects,
  getProject,
  createProject,
  updateProject,
  deleteProject,
  getProjectChats,
  addChatsToProject,
  removeChatFromProject,
  bulkRemoveProjectChats,
  testKeyword,
  getThemes,
  getAutoAssignmentConfiguration,
  setAutoAssignmentConfiguration,
  getAutoAssignmentStatus,
  runAutoAssignment,
  getProjectChatStats,
  getAvailableGroupsStats,
  getAutoAssignmentPreview,
  getGroupFilterOptions,
  getAvailableProvisioningAccounts,
  provisionProject,
  getProjectProvisioningLog,
  assignProjectHumanAdmin,
  revokeProjectHumanAdmin,
  getProjectCategories,
  generateProjectKeywords,
  getPreissueRelevanceSettings,
  updatePreissueRelevanceSettings,
  previewPreissueRelevance,
  createProjectCategory,
  updateProjectCategory,
  deleteProjectCategory,
  getProjectTestRunStatus,
  startProjectTestRun,
  refreshForumStatus,
  promoteToMain,
  attachToForum,
  migrateToForum,
};
